import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/api";
import { Minus, Plus, X, ArrowRight, ShoppingBag } from "lucide-react";

export default function Cart() {
    const { items, preview, updateQty, removeItem } = useCart();
    const navigate = useNavigate();

    if (items.length === 0) {
        return (
            <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 text-center" data-testid="cart-empty">
                <div className="h-20 w-20 rounded-full bg-[#F4EFE6] flex items-center justify-center mx-auto">
                    <ShoppingBag className="h-8 w-8 text-[#1B1B1F]" />
                </div>
                <h1 className="font-display text-4xl mt-6">Your bag is empty.</h1>
                <p className="text-muted-foreground mt-2">Discover the latest drop and build your fit.</p>
                <Link to="/shop" className="inline-flex mt-7 dw-btn-primary px-7 py-3 font-mono-ui">Start shopping</Link>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-10" data-testid="cart-page">
            <h1 className="font-display text-5xl md:text-6xl">Your bag</h1>
            <p className="text-muted-foreground mt-2">{preview.items.length} item{preview.items.length !== 1 && "s"}</p>

            <div className="mt-10 grid lg:grid-cols-[1fr_400px] gap-10">
                <div className="space-y-4">
                    {preview.items.map((it, i) => (
                        <motion.div
                            key={`${it.product_id}-${it.variant_sku}`}
                            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.04 }}
                            className="flex gap-4 sm:gap-6 rounded-[24px] border border-[#E7DED2] bg-white/70 p-4 sm:p-5"
                            data-testid={`cart-item-${i}`}
                        >
                            <Link to={`/product/${it.slug}`} className="shrink-0">
                                <img src={it.image} alt={it.title} className="h-24 w-24 sm:h-32 sm:w-32 object-cover rounded-2xl" />
                            </Link>
                            <div className="flex-1 min-w-0">
                                <Link to={`/product/${it.slug}`} className="font-medium hover:underline">{it.title}</Link>
                                <p className="text-sm text-muted-foreground mt-1">{it.color} · {it.size}</p>
                                <p className="text-sm font-semibold mt-2">{formatPrice(it.price)}</p>
                                {it.stock <= 5 && <p className="text-xs text-[#C07A5A] mt-1">Only {it.stock} left</p>}
                                <div className="mt-3 flex items-center justify-between">
                                    <div className="flex items-center rounded-full bg-[#F4EFE6] h-9">
                                        <button onClick={() => updateQty(it.product_id, it.variant_sku, it.qty - 1)} className="px-3 h-full hover:bg-[#E7DED2] rounded-l-full" data-testid={`cart-item-${i}-minus`}>
                                            <Minus className="h-3.5 w-3.5" />
                                        </button>
                                        <span className="px-4 text-sm font-medium min-w-[40px] text-center" data-testid={`cart-item-${i}-qty`}>{it.qty}</span>
                                        <button onClick={() => updateQty(it.product_id, it.variant_sku, it.qty + 1)} disabled={it.qty >= it.stock} className="px-3 h-full hover:bg-[#E7DED2] rounded-r-full disabled:opacity-40" data-testid={`cart-item-${i}-plus`}>
                                            <Plus className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                    <button onClick={() => removeItem(it.product_id, it.variant_sku)} className="text-xs text-muted-foreground hover:text-[#B42318] flex items-center gap-1" data-testid={`cart-item-${i}-remove`}>
                                        <X className="h-3.5 w-3.5" /> Remove
                                    </button>
                                </div>
                            </div>
                            <div className="hidden sm:block text-right">
                                <p className="font-semibold">{formatPrice(it.line_total)}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <aside>
                    <div className="sticky top-32 dw-section p-6" data-testid="cart-summary">
                        <h3 className="font-display text-2xl mb-5">Order summary</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">{formatPrice(preview.subtotal)}</span></div>
                            <div className="flex justify-between">
                                <span>Shipping</span>
                                <span>{preview.shipping === 0 ? <span className="text-[#2F7D5A] font-medium">Free</span> : formatPrice(preview.shipping)}</span>
                            </div>
                            {preview.subtotal > 0 && preview.subtotal < preview.free_shipping_threshold && (
                                <p className="text-xs text-muted-foreground bg-[#F4EFE6] rounded-2xl p-3 mt-2">
                                    Add {formatPrice(preview.free_shipping_threshold - preview.subtotal)} more for free express shipping.
                                </p>
                            )}
                        </div>
                        <div className="h-px bg-[#E7DED2] my-5" />
                        <div className="flex justify-between text-lg font-semibold">
                            <span>Total</span><span data-testid="cart-total">{formatPrice(preview.total)}</span>
                        </div>
                        <button onClick={() => navigate("/checkout")} className="mt-6 w-full dw-btn-primary h-12 font-mono-ui inline-flex items-center justify-center gap-2" data-testid="cart-checkout-button">
                            Checkout <ArrowRight className="h-4 w-4" />
                        </button>
                        <p className="text-[11px] text-center text-muted-foreground mt-4">Secure payment via Razorpay</p>
                    </div>
                </aside>
            </div>
            <div className="h-24" />
        </div>
    );
}
